import _ from 'underscore';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import state from '@bingads-webui-component-legacy/state/index';
import { getODataErrors } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import imageAspectRatios from '@bingads-webui-campaign-legacy/adextension-shared/imageAspectRatios';

class ImageRepository {
  constructor() {
    const aid = state.get('account_id');
    const cid = state.get('customer_id');

    this.baseURL = `/Customers(${cid})/Accounts(${aid})/`;
  }

  getImagesAsync({
    pageNumber = 0,
    pageSize = 1000,
  } = {}) {
    const top = pageSize;
    const skip = pageNumber * pageSize;
    const sortQuery = '$orderby=UploadedTime desc';
    const url = `${this.baseURL}Images?mediaenabledentityfilter=ImageAdExtension&${sortQuery}&$top=${top}&$skip=${skip}&$count=true`;

    return odata.get(url);
  }

  deleteImageAsync(imageId) {
    const url = `${this.baseURL}Images(${imageId})`;

    return odata.delete(url);
  }

  /**
   * Gets image specified by the id.
   * @param {number} imageId Id of the image to be requested.
   * @return {Promise} Response from the oData call.
   */
  getImageByIdAsync(imageId) {
    const url = `${this.baseURL}Images(${imageId})`;

    return odata.get(url);
  }

  getImagesByIdsAsync(imageIds, isImageFittingEnabled = false) {
    const reqs = _.map(imageIds, (imageId) => {
      return {
        type: 'GET',
        url: `${this.baseURL}Images(${imageId})${isImageFittingEnabled ? '?AdType=ImageAdExtension' : ''}`,
      };
    });

    return odata.batch({ path: '', reqs, needContentType: true }).then((response) => {
      return response.map((item) => item.data);
    });
  }

  uploadImagesAsync(images, activityLogger = null) {
    const processedImages = _.map(images, (image) => {
      const base64 = 'base64,';
      const base64Index = image.ImageData.indexOf(base64);
      const data = base64Index > -1 ?
        image.ImageData.substr(base64Index + base64.length) :
        image.ImageData;

      return {
        Data: data,
        Type: imageAspectRatios.imageEnumToType[Number.parseInt(image.AssetType, 10)],
      };
    });

    const url = `${this.baseURL}Images`;

    if (processedImages.length > 1) {
      const reqs = _.map(processedImages, (image) => {
        return {
          type: 'POST',
          url,
          data: image,
        };
      });

      return odata.batch({ path: '', reqs }).then((response) => this.processUploadedImagesBatch(response, activityLogger));
    }

    return odata.post(url, { data: processedImages[0] }).then(
      (image) => [{ imageId: image.value, errors: [] }],
      (error) => this.processUploadSingleImageRejection(error, activityLogger),
    );
  }

  /**
   * Process the different responses received for the batch call.
   * @param {object} response Responses for the oData call.
   * @param {object} activityLogger - object to log the activity
   *
   * @return {array} Array of objects containing the imageId and/or array of error
   * messages. If the imageId is provided, the object will contain an empty error
   * array too, but if there were errors, no imageId will be provided on the object.
   */
  processUploadedImagesBatch(response, activityLogger) {
    const cleanResponse = [];

    if (response.length > 0) {
      _.each(response, (r) => {
        const rClean = { errors: [], imageId: null };

        if (r.status === 200) {
          rClean.imageId = r.data.value;
        } else if (r.status === 400) {
          const errMsg = getODataErrors(r.data.value);

          _.each(errMsg, (err) => {
            rClean.errors.push(err.message);
          });

          if (activityLogger) {
            activityLogger.error(JSON.stringify(errMsg));
          }
        }
        cleanResponse.push(rClean);
      });
    }

    return cleanResponse;
  }

  uploadImageAsync(imageData, activityLogger = null) {
    if (activityLogger) {
      // Log upload image data
      const dataToLog = JSON.stringify(imageData).substr(0, 100);

      activityLogger.trace(JSON.stringify(dataToLog));
    }

    let processedImageData = imageData;

    const base64 = 'base64,';
    const base64Index = imageData.indexOf(base64);

    processedImageData = base64Index > -1 ?
      imageData.substr(base64Index + base64.length) :
      imageData;

    const url = `${this.baseURL}Images`;

    const requestBody = {
      Data: processedImageData,
      Type: 'GenericImage',
    };

    return odata.post(url, { data: requestBody }).then(
      (image) => ({ imageId: image.value, errors: [] }),
      (error) => this.processUploadSingleImageRejection(error, activityLogger),
    );
  }

  /**
   * Cleans the error response object and sets error messages into an array.
   * @param {object} error Object with errors returned by the oData call.
   * @param {Object} activityLogger - logger object for activity
   *
   * @return {array} Array of error messages.
   */
  processUploadSingleImageRejection(error, activityLogger) {
    if (activityLogger) {
      activityLogger.error(JSON.stringify(error));
    }

    return error.body.value;
  }
}

const imageRepository = new ImageRepository();

export default imageRepository;
